interface HeroSectionProps {
  onCtaClick: () => void;
}

export default function HeroSection({ onCtaClick }: HeroSectionProps) {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050D18]"
    >
      {/* Full-bleed cinematic background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f6c6b5af093b50df1e9b6d5c80ea6859.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        {/* Deep gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050D18]/60 via-[#050D18]/50 to-[#050D18]/85" />
        {/* Subtle bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#050D18] to-transparent" />
      </div>

      {/* Fine grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage:
            'linear-gradient(rgba(37,99,235,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.2) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      {/* Center content */}
      <div className="relative z-10 text-center max-w-5xl mx-auto px-8 lg:px-16 py-40 w-full">

        {/* Eyebrow */}
        <div className="animate-fade-in inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/8 border border-white/15 mb-10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#5EEAD4]" />
          <span className="text-xs tracking-widest uppercase text-white/65 font-medium">
            AI-Powered Lead Systems
          </span>
        </div>

        {/* Main headline */}
        <h1 className="animate-fade-up delay-200 text-[72px] lg:text-[108px] xl:text-[128px] font-semibold text-white leading-[0.95] tracking-[-0.03em] mb-8">
          Clarity in<br />Motion.
        </h1>

        {/* Subheadline */}
        <p className="animate-fade-up delay-300 text-lg lg:text-xl text-white/55 leading-relaxed max-w-xl mx-auto mb-12 tracking-wide font-normal">
          Henexa AI builds structured systems that capture, qualify, and convert
          your leads—automatically.
        </p>

        {/* CTAs */}
        <div className="animate-fade-up delay-400 flex items-center justify-center gap-4 flex-wrap">
          <button
            onClick={onCtaClick}
            className="px-8 py-3.5 bg-[#2563EB] text-white text-sm font-medium tracking-wide rounded-full hover:bg-[#1E3A8A] transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] active:scale-[0.98]"
          >
            Build Your System
          </button>
          <a
            href="#how-it-works"
            className="px-8 py-3.5 bg-white/10 border border-white/20 text-white text-sm font-medium tracking-wide rounded-full hover:bg-white/18 transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] active:scale-[0.98]"
          >
            See How It Works
          </a>
        </div>

        {/* Stats strip */}
        <div className="animate-fade-up delay-600 mt-24 pt-10 border-t border-white/10 flex items-center justify-center gap-16 flex-wrap">
          {[
            { value: '3×', label: 'Faster Lead Response' },
            { value: '85%', label: 'Booking Rate Increase' },
            { value: '24/7', label: 'Automated Operations' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl font-semibold text-white tracking-tight">{stat.value}</div>
              <div className="text-xs text-white/40 mt-1 tracking-widest uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in delay-700">
        <span className="text-xs text-white/30 tracking-widest uppercase">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/20 to-transparent" />
      </div>
    </section>
  );
}
